import {noop} from '@benchling/js-utils';
import $ from 'jquery';
import moment from 'moment';
import React from 'react';
import _ from 'underscore';

import * as analytics from 'analytics/Analytics';
import {makeAnalyticsEventId} from 'analytics/Analytics';
import {assertAndContinue} from 'Asserts';
import {DataModificationsHeader} from 'audit/DataModifications';
import {callAsyncWithDataModifications} from 'audit/DataModificationsModal';
import BenchlingConfig from 'BenchlingConfig';
import {pluralize, redirectForDownload} from 'BenchlingUtil';
import {ANALYTICS_MODULE} from 'constants/AnalyticsConstants';
import {EventConstants} from 'constants/EventConstants';
import * as FileBrowserConstants from 'constants/FileBrowserConstants';
import {FolderSummary} from 'constants/FolderConstants';
import ModelTypeConstants, {StorableItemType} from 'constants/ModelTypeConstants';
import {Section} from 'constants/NavConstants';
import {
  BioentityAliasInfo,
  FrontendLabelingStrategy,
  LabelingStrategy,
  OligoAwareFolderItemType,
  SchemalessBulkImportSupportedOligoAwareTypes,
} from 'constants/RegistryConstants';
import {Source} from 'constants/SourceConstants';
import FolderDAOCache from 'file-browser/FolderDAOCache';
import {RegistryLabelingStrategy} from 'graphql-client/__generated/graphqlGlobalTypes';
import {writeFragmentsToCache} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import * as ConnectFilesDAO from 'instruments/connections/ConnectFilesDAO';
import AppDispatcher from 'lib/Dispatcher';
import listFolderSection from 'nav/sidepanel/listFolderSection';
import listRecentlyCreatedStorables from 'nav/sidepanel/listing/listRecentlyCreatedStorables';
import {getNavigationURL} from 'nav/sidepanel/listing/SearchURLs';
import {getSelectedPageSize} from 'nav/sidepanel/PageSizeUtil';
import {openListing} from 'nav/sidepanel/SidepanelReduxActions';
import {
  DATA_TABLE_TYPE,
  DataTable,
  DataTableConfig,
  ImportedRow as ImportedDataTableRow,
  ImportedRowUpdate,
} from 'notebook/data-tables/DataTableConstants';
import * as DataTableUtil from 'notebook/data-tables/DataTableUtil';
import {ImportedStructuredTableRow} from 'notebook/structured-tables/spreadsheet-import/prepareImportedRows';
import {ImportedTransferTableRow} from 'notebook/transfer-tables/TransferTableUtil';
import notifications from 'notifications';
import {logProfilerTimingEvent} from 'performance/BenchlingProfiler';
import {
  TRIAL_SAMPLE_IMPORT_STARTED_EVENT,
  TRIAL_SAMPLE_IMPORT_STARTED_FOR_TRAY_TASK_EVENT,
  TRIAL_SAMPLE_IMPORT_FINISHED_EVENT,
  TRIAL_SAMPLE_VALIDATION_FINISHED_EVENT,
  TRIAL_SAMPLE_VALIDATION_STARTED_EVENT,
  TRIAL_SAMPLE_IMPORT_TRAY_TASK_VISIBLE_EVENT,
} from 'performance/processRegistryEvents';
import {getStore} from 'redux-stores/Store';
import {
  DataModsItemType,
  getInventoryRegistrySpreadsheetChangeSummary,
  getRegisterMergeOnlyChangeSummary,
  InventoryRegistryDataModAction,
} from 'samples/getInventoryRegistryChangeSummary';
import {Filter, FilterKey, Operator, SearchParams} from 'search/SearchConstants';
import * as TrayTaskActions from 'tray/TrayTaskActions';
import * as TrayTaskConstants from 'tray/TrayTaskConstants';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import * as LegacyModalContainer from 'ui-library/LegacyModalContainer';
import * as LoadingScreen from 'ui-library/LoadingScreen';
import {PYTHON_ISOFORMAT_WITH_TIMEZONE, CANONICAL_FORMAT} from 'util/DateConstants';
import {asJQueryPromise, withLoadingScreen} from 'util/Promises';
import {Optional} from 'util/Types';
import {ImportedRow} from 'workflows/samples/makeSampleGroup';

import {FileListingGraphQL} from '../../graphql-fragments/files/__generated/FileListingGraphQL';
import {MonomerMapping} from './panes/MonomerMappingForm';
import {
  COLUMN_TYPE,
  DerivedColumn,
  DerivedColumnsMapping,
  IMPORT_TYPE,
  ImportSourceType,
  OPTIONS,
  Pane,
  PaneKey,
  RAW_TEXT_SPREADSHEET_NAME,
  RegistryImportSettingsProps,
  RowErrorCode,
  SelectedSchemaIds,
  SPREADSHEET_BULK_IMPORT_KEY,
  SpreadsheetColumn,
  ValidateSpreadsheetForBulkImportResponse,
} from './SpreadsheetBulkImportConstants';
import * as SpreadsheetBulkImportDAO from './SpreadsheetBulkImportDAO';
import {CreateTrialSamplesFromSpreadsheetSuccessResponse} from './SpreadsheetBulkImportDAO';
import SpreadsheetBulkImportStore, {
  BulkImportOptions,
  ImportSettings,
  RegistryImportSettings,
} from './SpreadsheetBulkImportStore';
import * as SpreadsheetBulkImportUtil from './SpreadsheetBulkImportUtil';
import {getImportEntityType} from './SpreadsheetBulkImportUtil';

function getServerColumnTypes(columnTypes: Array<COLUMN_TYPE>): Array<COLUMN_TYPE> {
  return columnTypes.map((colType) => SpreadsheetBulkImportUtil.getColumnTypeToSendToServer(colType));
}

// CONVERT_IDS does not exist on the backend, it is treated as IDS_FROM_NAMES
function getServerLabelingStrategy(
  labelingStrategy: LabelingStrategy | null
): RegistryLabelingStrategy | null {
  return labelingStrategy === FrontendLabelingStrategy.CONVERT_IDS
    ? RegistryLabelingStrategy.IDS_FROM_NAMES
    : labelingStrategy != null
      ? RegistryLabelingStrategy[labelingStrategy]
      : null;
}

function sanitizeRegistryImportSettingsForServer({
  inOrgCollection,
  inUserCollection,
  labelingStrategy,
  trialSubjectLabelingStrategy,
}: RegistryImportSettingsProps): RegistryImportSettings {
  const sanitizedRegistryImportSettings: RegistryImportSettings = {
    inOrgCollection,
    inUserCollection,
    labelingStrategy: null,
    trialSubjectLabelingStrategy: null,
  };

  if (labelingStrategy !== undefined) {
    sanitizedRegistryImportSettings.labelingStrategy = getServerLabelingStrategy(labelingStrategy);
  }

  if (trialSubjectLabelingStrategy !== undefined) {
    sanitizedRegistryImportSettings.trialSubjectLabelingStrategy = getServerLabelingStrategy(
      trialSubjectLabelingStrategy
    );
  }

  return sanitizedRegistryImportSettings;
}

function getCustomSyntaxIdFromColumns(columns: Array<SpreadsheetColumn>): string | undefined {
  const customSyntaxColumn = columns.find((c) => c.type === COLUMN_TYPE.CUSTOM_MOD_SYNTAX);
  return customSyntaxColumn ? customSyntaxColumn.customSyntaxId : undefined;
}

/**
 * Return selected source ID from the store to associate with new storables and to find existing ones.
 */
function getSourceIdForStorableBulkAction(): Optional<string> {
  return SpreadsheetBulkImportStore.getState().registryImportSettings.projectId;
}

function forceCloseModal(): void {
  LegacyModalContainer.close(SPREADSHEET_BULK_IMPORT_KEY);
  _.defer(() => {
    AppDispatcher.dispatch({
      actionType: EventConstants.SPREADSHEET_BULK_IMPORT_RESET,
    });
  });
}

export function closeModal(): void {
  if (SpreadsheetBulkImportStore.getState().shouldCloseOnRequest) {
    forceCloseModal();
  }
}

export function changeImportSourceType(value: ImportSourceType): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.SPREADSHEET_BULK_IMPORT_SOURCE_TYPE_CHANGED,
    value,
  });
}

export function updateRawText(rawText: string): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.SPREADSHEET_BULK_IMPORT_RAW_TEXT_UPDATED,
    rawText,
  });
}

export function uploadAndParseSDF(file: File): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.SPREADSHEET_BULK_IMPORT_UPLOAD_AND_PARSE,
    file,
  });
  SpreadsheetBulkImportDAO.importFile(file)
    .then((url: string) => SpreadsheetBulkImportDAO.parseSDF(file.name, url))
    .done((response: SpreadsheetBulkImportDAO.ParseSDFResponse) => {
      AppDispatcher.dispatch({
        actionType: EventConstants.SPREADSHEET_BULK_IMPORT_UPLOAD_AND_PARSE_COMPLETED,
        file,
        url: response.updatedUrl,
        columnNames: response.columnNames,
      });
    })
    .fail((response) => {
      notifications.notifyError(response, {duration: 0});
      AppDispatcher.dispatch({
        actionType: EventConstants.SPREADSHEET_BULK_IMPORT_UPLOAD_AND_PARSE_FAILED,
        errorMessage: notifications.getFriendlyError(response),
        file,
      });
    });
}

export function uploadAndParse(
  file: File,
  {
    onSuccessAnalyticsCallback = noop,
    onFailAnalyticsCallback = noop,
  }: {
    onSuccessAnalyticsCallback?: () => void;
    onFailAnalyticsCallback?: (errorMessage: string | null) => void;
  } = {},
  maxAllowedRowsToImport?: number,
  inferDataTypes?: boolean
): void {
  /* Callbacks can optionally be provided to capture analytics for upload/parse success or failure.
    See UploadSpreadsheetModalPane.tsx for type definitions
   */
  AppDispatcher.dispatch({
    actionType: EventConstants.SPREADSHEET_BULK_IMPORT_UPLOAD_AND_PARSE,
    file,
  });
  SpreadsheetBulkImportDAO.importFile(file)
    .then((url: string) =>
      $.when<string | {columns: Array<string>; row_count: number}>(
        url,
        SpreadsheetBulkImportDAO.parseSpreadsheet(file.name, url, inferDataTypes)
      )
    )
    .done((url, [response]) => {
      // Subtract by one as we do not insert the spreadsheet's header row to the table.
      const rowsToAddCount = response.row_count - 1;
      if (maxAllowedRowsToImport != null && rowsToAddCount > maxAllowedRowsToImport) {
        const errorMessage = `Your file contains ${rowsToAddCount} rows, exceeding the limit of ${maxAllowedRowsToImport}. Please reduce the number of rows and try again.`;
        AppDispatcher.dispatch({
          actionType: EventConstants.SPREADSHEET_BULK_IMPORT_UPLOAD_AND_PARSE_FAILED,
          errorMessage,
          file,
        });
        onFailAnalyticsCallback(errorMessage);
      } else {
        AppDispatcher.dispatch({
          actionType: EventConstants.SPREADSHEET_BULK_IMPORT_UPLOAD_AND_PARSE_COMPLETED,
          file,
          url,
          columnNames: response.columns,
          ...(response.data_types && {dataTypes: response.data_types}),
        });
        onSuccessAnalyticsCallback();
      }
    })
    .fail((response) => {
      const errorMessage = notifications.getFriendlyError(response);
      notifications.notifyError(response, {duration: 0});
      AppDispatcher.dispatch({
        actionType: EventConstants.SPREADSHEET_BULK_IMPORT_UPLOAD_AND_PARSE_FAILED,
        errorMessage,
        file,
      });
      onFailAnalyticsCallback(errorMessage);
    });
}

export function uploadAndParseConnectionFiles(
  connectionFile: FileListingGraphQL,
  adapterId?: string,
  transformDataType?: string | null,
  delimiter?: string,
  inferDataTypes?: boolean
): void {
  /* Callbacks can optionally be provided to capture analytics for upload/parse success or failure.
    See UploadSpreadsheetModalPane.tsx for type definitions
   */
  AppDispatcher.dispatch({
    actionType: EventConstants.SPREADSHEET_BULK_IMPORT_UPLOAD_AND_PARSE,
    file: connectionFile,
  });
  ConnectFilesDAO.getSpreadsheetColumns(
    connectionFile.id,
    adapterId ?? '',
    transformDataType ?? '',
    delimiter,
    inferDataTypes
  )
    .then((response) => {
      AppDispatcher.dispatch({
        actionType: EventConstants.SPREADSHEET_BULK_IMPORT_UPLOAD_AND_PARSE_COMPLETED,
        file: connectionFile,
        url: connectionFile.url,
        columnNames: response.columns,
        ...(response.data_types && {dataTypes: response.data_types}),
      });
    })
    .catch((response) => {
      const errorMessage = notifications.getFriendlyError(response);
      notifications.notifyError(response, {duration: 0});
      AppDispatcher.dispatch({
        actionType: EventConstants.SPREADSHEET_BULK_IMPORT_UPLOAD_AND_PARSE_FAILED,
        errorMessage,
        file: connectionFile,
      });
    });
}

export function findDerivedColumns(): void {
  const {
    file,
    fileData,
    selectedDestination,
    linkFolderIds: linkItemsFolderIds,
    selectedSchemaIds,
    schemalessCreationEntityType,
    options,
    _timeLimit,
  } = SpreadsheetBulkImportStore.getState();
  const url = fileData!.url;
  const columns = fileData!.columns;

  const columnTypes = SpreadsheetBulkImportUtil.pluckColumnType(columns);
  const serverColumnTypes = getServerColumnTypes(columnTypes);
  const customSyntaxId = getCustomSyntaxIdFromColumns(columns);
  const schemaFieldIdsByColumnName = SpreadsheetBulkImportUtil.getSchemaFieldIdsByColumnName(columns);
  const inventoryFolderId = selectedDestination ? selectedDestination.folderId : null;

  const spreadsheetParams = {
    filename: file!.name,
    columnTypes: serverColumnTypes,
    location: url,
    _timeLimit,
  };
  AppDispatcher.dispatch({
    actionType: EventConstants.SPREADSHEET_BULK_IMPORT_FIND_DERIVED_COLUMNS,
  });
  SpreadsheetBulkImportDAO.findDerivedColumns(
    spreadsheetParams,
    inventoryFolderId,
    linkItemsFolderIds,
    selectedSchemaIds.tagSchemaId,
    schemalessCreationEntityType,
    options[OPTIONS.ARE_SEQUENCES_CIRCULAR],
    schemaFieldIdsByColumnName,
    customSyntaxId
  )
    .then((derivedColumnsResponse) => {
      AppDispatcher.dispatch({
        actionType: EventConstants.SPREADSHEET_BULK_IMPORT_FIND_DERIVED_COLUMNS_COMPLETED,
        derivedColumnsResponse,
      });
    })
    .fail((response) => {
      AppDispatcher.dispatch({
        actionType: EventConstants.SPREADSHEET_BULK_IMPORT_FIND_DERIVED_COLUMNS_FAILED,
      });
    });
}

interface DaoValidateArgs {
  inventoryFolderId: string | null;
  linkItemsFolderIds: Array<string>;
  selectedSchemaIds: SelectedSchemaIds;
  schemalessCreationEntityType: SchemalessBulkImportSupportedOligoAwareTypes | null;
  schemaFieldIdsByColumnName: {[name: string]: number};
  options: BulkImportOptions;
  derivedColumnsMapping: DerivedColumnsMapping;
  monomerMapping: MonomerMapping | null;
  customSyntaxId: string | undefined;
  complexPolymerComponentColumnMapping: {[columnName: string]: string};
}

interface SpreadsheetParams {
  filename: string;
  columnTypes: Array<COLUMN_TYPE>;
  location: string;
  _timeLimit: number | null;
}

export function dispatchForValidateSpreadsheet(
  daoValidate: (
    spreadsheetParams: SpreadsheetParams,
    args: DaoValidateArgs
  ) => JQueryPromise<ValidateSpreadsheetForBulkImportResponse>
): void {
  // We pass in file so that that store can ignore XHRs for stale requests.
  const {
    file,
    fileData,
    derivedColumnsData: {derivedColumns},
    monomerMapping,
    selectedDestination,
    linkFolderIds: linkItemsFolderIds,
    selectedSchemaIds,
    schemalessCreationEntityType,
    options,
    _timeLimit,
  } = SpreadsheetBulkImportStore.getState();
  const url = fileData!.url;
  const columns = fileData!.columns;

  const columnTypes = SpreadsheetBulkImportUtil.pluckColumnType(columns);
  const serverColumnTypes = getServerColumnTypes(columnTypes);
  const customSyntaxId = getCustomSyntaxIdFromColumns(columns);
  const schemaFieldIdsByColumnName = SpreadsheetBulkImportUtil.getSchemaFieldIdsByColumnName([
    ...columns,
    ...(derivedColumns ?? []),
  ]);

  const inventoryFolderId = selectedDestination ? selectedDestination.folderId : null;
  const derivedColumnsMapping = SpreadsheetBulkImportUtil.getDerivedColumnsMapping(derivedColumns ?? []);
  const complexPolymerComponentColumnMapping =
    SpreadsheetBulkImportUtil.getComplexPolymerComponentColumnMapping(columns);

  AppDispatcher.dispatch({
    actionType: EventConstants.SPREADSHEET_BULK_IMPORT_VALIDATE,
  });

  const spreadsheetParams = {
    filename: file!.name,
    columnTypes: serverColumnTypes,
    location: url!,
    _timeLimit,
  };
  daoValidate(spreadsheetParams, {
    inventoryFolderId,
    linkItemsFolderIds,
    selectedSchemaIds,
    schemalessCreationEntityType,
    schemaFieldIdsByColumnName,
    options,
    derivedColumnsMapping,
    monomerMapping,
    customSyntaxId,
    complexPolymerComponentColumnMapping,
  })
    .then(
      ({
        itemNameErrors,
        registryErrors,
        registryWarnings,
        otherErrors,
        otherWarnings,
        studyWarnings,
        tagErrors,
      }: ValidateSpreadsheetForBulkImportResponse) => {
        AppDispatcher.dispatch({
          actionType: EventConstants.SPREADSHEET_BULK_IMPORT_VALIDATE_COMPLETED,
          url,
          itemNameErrors,
          registryErrors,
          registryWarnings,
          otherErrors,
          otherWarnings,
          studyWarnings,
          tagErrors,
        });
      }
    )
    .fail((response) => {
      notifications.notifyError(response, {duration: 0});
      AppDispatcher.dispatch({
        actionType: EventConstants.SPREADSHEET_BULK_IMPORT_VALIDATE_FAILED,
        url,
      });
    });
}

export function validateSpreadsheetForUpdateBioentities(): void {
  dispatchForValidateSpreadsheet(
    (
      spreadsheetParams,
      {
        inventoryFolderId,
        linkItemsFolderIds,
        selectedSchemaIds,
        schemaFieldIdsByColumnName,
        derivedColumnsMapping,
        monomerMapping,
        options,
        customSyntaxId,
        complexPolymerComponentColumnMapping,
      }
    ) =>
      SpreadsheetBulkImportDAO.validateSpreadsheetForUpdateBioentities(
        spreadsheetParams,
        inventoryFolderId!,
        linkItemsFolderIds,
        selectedSchemaIds.tagSchemaId!,
        schemaFieldIdsByColumnName,
        options[OPTIONS.DELETE_EXISTING_ALIASES],
        derivedColumnsMapping,
        monomerMapping,
        complexPolymerComponentColumnMapping,
        customSyntaxId
      )
  );
}

export function validateSpreadsheetForCreateInventoryFiles(): void {
  dispatchForValidateSpreadsheet(
    (
      spreadsheetParams,
      {
        inventoryFolderId,
        linkItemsFolderIds,
        selectedSchemaIds,
        schemalessCreationEntityType,
        schemaFieldIdsByColumnName,
        options,
        derivedColumnsMapping,
        monomerMapping,
        customSyntaxId,
        complexPolymerComponentColumnMapping,
      }
    ) =>
      SpreadsheetBulkImportDAO.validateSpreadsheetForCreateInventoryFiles(
        spreadsheetParams,
        inventoryFolderId!,
        linkItemsFolderIds,
        selectedSchemaIds.tagSchemaId,
        selectedSchemaIds.tagSchemaId == null ? schemalessCreationEntityType : null,
        options[OPTIONS.ARE_SEQUENCES_CIRCULAR],
        options[OPTIONS.IMPORT_CONTAINERS] ? selectedSchemaIds.containerSchemaId : null,
        schemaFieldIdsByColumnName,
        derivedColumnsMapping,
        monomerMapping,
        complexPolymerComponentColumnMapping,
        customSyntaxId
      )
  );
}

export function validateSpreadsheetForCreateRegistryEntities(): void {
  dispatchForValidateSpreadsheet(
    (
      spreadsheetParams,
      {
        inventoryFolderId,
        linkItemsFolderIds,
        selectedSchemaIds,
        schemaFieldIdsByColumnName,
        options,
        derivedColumnsMapping,
        monomerMapping,
        customSyntaxId,
        complexPolymerComponentColumnMapping,
      }
    ) => {
      const registryImportSettings = SpreadsheetBulkImportStore.getState().registryImportSettings;
      return SpreadsheetBulkImportDAO.validateSpreadsheetForCreateRegistryEntities(
        spreadsheetParams,
        registryImportSettings.registry!.id,
        inventoryFolderId,
        linkItemsFolderIds,
        selectedSchemaIds.tagSchemaId!,
        options[OPTIONS.ARE_SEQUENCES_CIRCULAR],
        options[OPTIONS.IMPORT_CONTAINERS] ? selectedSchemaIds.containerSchemaId : null,
        sanitizeRegistryImportSettingsForServer(registryImportSettings),
        schemaFieldIdsByColumnName,
        derivedColumnsMapping,
        monomerMapping,
        complexPolymerComponentColumnMapping,
        customSyntaxId
      );
    }
  );
}

export function validateCreateResultsForRun(assayRunId: string, resultSchemaId: string): void {
  dispatchForValidateSpreadsheet((spreadsheetParams, {schemaFieldIdsByColumnName}) =>
    SpreadsheetBulkImportDAO.validateCreateResultsForRun(
      spreadsheetParams,
      assayRunId,
      resultSchemaId,
      schemaFieldIdsByColumnName
    )
  );
}

export function createResultsForRun(assayRunId: string, resultSchemaId: string, onSuccess: () => void): void {
  LoadingScreen.show('Uploading...');

  dispatchForImportFromSpreadsheet((spreadsheetParams, {schemaFieldIdsByColumnName}, dataModsHeader) =>
    SpreadsheetBulkImportDAO.createResultsForRun(
      spreadsheetParams,
      assayRunId,
      resultSchemaId,
      schemaFieldIdsByColumnName,
      dataModsHeader
    )
      .done(() => {
        onSuccess();
      })
      .always(() => {
        LoadingScreen.hide();
      })
  );
}

export function validateUpdatedRowsForDataTableImport(dataTable: DataTable): void {
  dispatchForValidateSpreadsheet(
    (spreadsheetParams, {selectedSchemaIds, schemaFieldIdsByColumnName, options}) => {
      const columns = SpreadsheetBulkImportStore.getState().fileData!.columns;
      const tableColumnNamesByColumnName = SpreadsheetBulkImportUtil.getTableColumnNamesByColumnName(columns);
      return SpreadsheetBulkImportDAO.validateUpdatedRowsForDataTableImport(
        spreadsheetParams,
        tableColumnNamesByColumnName,
        dataTable
      );
    }
  );
}

export function validateNewRowsForDataTableImport(
  entryOrStageEntryId: string,
  dataTableConfig: DataTableConfig
): void {
  const stageEntryId =
    DataTableUtil.getDataTableConfigType(dataTableConfig) === DATA_TABLE_TYPE.STAGE_RUN_SAMPLE_LINK_TABLE
      ? entryOrStageEntryId
      : null;

  dispatchForValidateSpreadsheet(
    (spreadsheetParams, {selectedSchemaIds, schemaFieldIdsByColumnName, options}) => {
      const columns = SpreadsheetBulkImportStore.getState().fileData!.columns;
      const tableColumnNamesByColumnName = SpreadsheetBulkImportUtil.getTableColumnNamesByColumnName(columns);
      return SpreadsheetBulkImportDAO.validateNewRowsForDataTableImport(
        spreadsheetParams,
        tableColumnNamesByColumnName,
        dataTableConfig,
        stageEntryId
      );
    }
  );
}

export function validateNewRowsForTransferTableImport(fileId: string): void {
  dispatchForValidateSpreadsheet((spreadsheetParams, {schemaFieldIdsByColumnName}) =>
    SpreadsheetBulkImportDAO.validateNewRowsForTransferTableImport(
      spreadsheetParams,
      schemaFieldIdsByColumnName,
      fileId
    )
  );
}

export function validateNewRowsForRegistrationTableImport(): void {
  dispatchForValidateSpreadsheet((spreadsheetParams, {schemaFieldIdsByColumnName}) =>
    SpreadsheetBulkImportDAO.validateNewRowsForRegistrationTableImport(
      spreadsheetParams,
      schemaFieldIdsByColumnName
    )
  );
}

export function validateRowsForStructuredTableSpreadsheetImport(): void {
  dispatchForValidateSpreadsheet((spreadsheetParams, {schemaFieldIdsByColumnName}) =>
    SpreadsheetBulkImportDAO.validateNewRowsForStructuredTableImport(
      spreadsheetParams,
      schemaFieldIdsByColumnName
    )
  );
}

const IMPORT_VERB = 'Imported';
const UPDATE_VERB = 'Updated';

function getTaskSubheading(verb: string, modelType: string, location: string): string {
  const pluralizedType = modelType ? pluralize(2, modelType) : '';
  if (verb === IMPORT_VERB) {
    return `${IMPORT_VERB} ${pluralizedType} to ${location}`;
  } else {
    return `${verb} ${pluralizedType} in ${location}`;
  }
}

interface TrayTaskResponse {
  folder: FolderSummary;
  userMessage?: string;
}

function registerTrayTask(
  xhr: JQueryPromise<TrayTaskResponse>,
  heading: string,
  getSubheading: (folder: FolderSummary) => string,
  onSubheadingClick: (folder: FolderSummary) => void
): void {
  const taskId = TrayTaskActions.createTrayTask({
    icon: <BenchlingIcon icon="table-list" />,
    heading,
    progress: TrayTaskConstants.INDETERMINATE_PROGRESS,
  });
  xhr.done(({folder, userMessage}: TrayTaskResponse) => {
    FolderDAOCache.invalidateFolderContents(folder.api_identifier);
    TrayTaskActions.updateTrayTask(taskId, {
      progress: TrayTaskConstants.SUCCESS_PROGRESS,
      subheading: getSubheading(folder),
      onSubheadingClick: () => {
        onSubheadingClick(folder);
      },
    });
  });
  xhr.fail(() => {
    TrayTaskActions.updateTrayTask(taskId, {
      progress: TrayTaskConstants.FAILURE_PROGRESS,
      error: 'An error occurred while importing the file. Please try again.',
    });
  });
}

/**
 * Build search params for listing items created by trial sample ingestion.
 *
 * Filters on created at, creator, and type of item. If there are few enough items, also filters on name.
 * To decide whether there are few enough items to filter on name, it checks the length of the resulting URL
 * for expanded search. If it would be longer than 2000 characters, then it omits the name filter.
 *
 * @param response CreateTrialSamplesFromSpreadsheetSuccessResponse
 */
function buildTrialSamplesSearchParams(
  response: CreateTrialSamplesFromSpreadsheetSuccessResponse
): SearchParams {
  const start = moment(response.start, [PYTHON_ISOFORMAT_WITH_TIMEZONE]); // rounded down autom'cly
  const finish = moment(response.finish, [PYTHON_ISOFORMAT_WITH_TIMEZONE]);
  const finishRoundedUp = finish.add(1, 'second');
  const searchFiltersWithoutNames: Array<Filter> = [
    // TODO(BNCH-107495): Add creator filter once we have creator set on test orders
    // {key: FilterKey.CREATOR, operator: Operator.IS, value: [response.creator]},
    {key: FilterKey.IN_STUDY, operator: Operator.IS_ONE_OF, value: response.studies.map(({id}) => id)},
    {
      key: FilterKey.CREATED_AT,
      operator: Operator.BETWEEN,
      value: {from: start.format(CANONICAL_FORMAT), to: finishRoundedUp.format(CANONICAL_FORMAT)},
    },
    {
      key: FilterKey.TYPES,
      operator: Operator.IS_ONE_OF,
      value: [
        ModelTypeConstants.CONTAINER,
        ModelTypeConstants.BASIC_FOLDER_ITEM,
        ModelTypeConstants.PROCESS_TASK,
        ModelTypeConstants.TEST_ORDER,
      ],
    },
  ];
  const searchParamsWithoutNames: SearchParams = {
    filters: searchFiltersWithoutNames,
    limit: getSelectedPageSize(),
    offset: 0,
    query: '',
    sorts: [],
  };

  const searchParamsWithNames: SearchParams = {
    ...searchParamsWithoutNames,
    filters: [
      ...searchFiltersWithoutNames,
      {
        key: FilterKey.NAMES,
        operator: Operator.IS_ONE_OF,
        value: [
          ...response.containers.map(({name}) => name),
          ...response.newSamples.map(({name}) => name),
          ...response.newSubjects.map(({name}) => name),
          ...response.discrepancies.map(({name}) => name),
          ...response.testOrders.map(({name}) => name),
        ],
      },
    ],
  };

  if (getNavigationURL('/search', searchParamsWithNames, [null, null, null, null]).length < 2000) {
    return searchParamsWithNames;
  } else {
    return searchParamsWithoutNames;
  }
}

function registerTrayTaskForSampleIngestion(
  filename: string,
  xhr: JQueryPromise<CreateTrialSamplesFromSpreadsheetSuccessResponse>
): void {
  const heading = filename === RAW_TEXT_SPREADSHEET_NAME ? 'Importing' : `Importing ${filename}`;
  const taskId = TrayTaskActions.createTrayTask({
    icon: <BenchlingIcon icon="table-list" />,
    heading,
    progress: TrayTaskConstants.INDETERMINATE_PROGRESS,
  });
  xhr.done((response: CreateTrialSamplesFromSpreadsheetSuccessResponse) => {
    // We have the "Open" button make a best effort to show the items that were created by this ingestion,
    // including containers, samples, subjects, discrepancies, and test orders
    TrayTaskActions.updateTrayTask(taskId, {
      progress: TrayTaskConstants.SUCCESS_PROGRESS,
      subheading: 'Imported trial samples',
      onSubheadingClick: () => {
        const searchParams = buildTrialSamplesSearchParams(response);
        getStore().dispatch(openListing(Section.SEARCH, searchParams));
      },
    });
  });
  xhr.fail(() => {
    TrayTaskActions.updateTrayTask(taskId, {
      progress: TrayTaskConstants.FAILURE_PROGRESS,
      error: 'An error occurred while importing the file. Please try again.',
    });
  });
}

function registerTrayTaskForStorables(
  filename: string,
  verb: string,
  modelType: StorableItemType | 'transfer',
  xhr: JQueryPromise<TrayTaskResponse>
): void {
  assertAndContinue([IMPORT_VERB, UPDATE_VERB].includes(verb), `Unexpected verb ${verb}`);
  const heading = filename === RAW_TEXT_SPREADSHEET_NAME ? 'Importing' : `Importing ${filename}`;
  registerTrayTask(
    xhr,
    heading,
    () => getTaskSubheading(verb, modelType, 'inventory'),
    (folder) => {
      // For bulk storable endpoints, `folder` should be the registry root folder.
      listRecentlyCreatedStorables(folder, modelType);
    }
  );
}

function registerTrayTaskForFolderItems(
  filename: string,
  verb: string,
  modelType: string,
  xhr: JQueryPromise<TrayTaskResponse>
): void {
  assertAndContinue([IMPORT_VERB, UPDATE_VERB].includes(verb), `Unexpected verb ${verb}`);
  const heading = filename === RAW_TEXT_SPREADSHEET_NAME ? 'Importing' : `Importing ${filename}`;
  registerTrayTask(
    xhr,
    heading,
    (folder) => getTaskSubheading(verb, modelType, folder.name),
    (folder) => {
      listFolderSection(folder, FileBrowserConstants.INVENTORY_SECTION);
    }
  );
}

interface DaoImportArgs extends DaoValidateArgs {
  registryImportSettings: RegistryImportSettingsProps;
}

export function dispatchForImportFromSpreadsheet(
  daoImport: (
    spreadsheetParams: SpreadsheetParams & {rowsToSkip: Array<number> | null},
    args: DaoImportArgs,
    dataModsHeader?: DataModificationsHeader
  ) => JQueryPromise<{}>,
  dataModsHeader?: DataModificationsHeader
): void {
  const {
    errorData,
    file,
    fileData,
    derivedColumnsData: {derivedColumns},
    monomerMapping,
    importType,
    selectedDestination,
    linkFolderIds: linkItemsFolderIds,
    selectedSchemaIds,
    schemalessCreationEntityType,
    spreadsheetColumnsMappingName,
    options,
    _timeLimit,
    registryImportSettings,
  } = SpreadsheetBulkImportStore.getState();
  const secondsSpentOnPanes = SpreadsheetBulkImportStore.finalizeTimeTrackingAndReturnSecondsSpentOnPanes();
  const url = fileData!.url;
  const columns = fileData!.columns;

  const columnTypes = SpreadsheetBulkImportUtil.pluckColumnType(columns);
  const serverColumnTypes = getServerColumnTypes(columnTypes);
  const customSyntaxId = getCustomSyntaxIdFromColumns(columns);
  const schemaFieldIdsByColumnName = SpreadsheetBulkImportUtil.getSchemaFieldIdsByColumnName([
    ...columns,
    ...(derivedColumns ?? []),
  ]);
  const inventoryFolderId = selectedDestination ? selectedDestination.folderId : null;
  let rowsToSkip = null;
  if (options[OPTIONS.SKIP_ERRORS]) {
    rowsToSkip = SpreadsheetBulkImportUtil.getRowsToSkip(errorData, options);
  }
  const derivedColumnsMapping = SpreadsheetBulkImportUtil.getDerivedColumnsMapping(derivedColumns ?? []);
  const complexPolymerComponentColumnMapping =
    SpreadsheetBulkImportUtil.getComplexPolymerComponentColumnMapping(columns);

  const spreadsheetParams = {
    filename: file!.name,
    location: url!,
    columnTypes: serverColumnTypes,
    rowsToSkip,
    _timeLimit,
  };
  daoImport(
    spreadsheetParams,
    {
      inventoryFolderId,
      linkItemsFolderIds,
      selectedSchemaIds,
      schemalessCreationEntityType,
      schemaFieldIdsByColumnName,
      options,
      derivedColumnsMapping,
      registryImportSettings,
      monomerMapping,
      customSyntaxId,
      complexPolymerComponentColumnMapping,
    },
    dataModsHeader
  )
    .then(() => {
      const {tagSchemaId} = selectedSchemaIds;
      analytics.track('bulk_spreadsheet', 'import_completed', {
        importType,
        schemalessCreationEntityType,
        schemaEntityType: tagSchemaId
          ? getImportEntityType(selectedSchemaIds, schemalessCreationEntityType)
          : null,
        tagSchemaId,
        secondsSpentOnPanes,
        nameOfColumnsMapping: spreadsheetColumnsMappingName,
      });
    })
    .fail((response) => {
      const {tagSchemaId} = selectedSchemaIds;
      notifications.notifyError(response, {duration: 0});
      analytics.track('bulk_spreadsheet', 'import_failed', {
        importType,
        schemalessCreationEntityType,
        schemaEntityType: tagSchemaId
          ? getImportEntityType(selectedSchemaIds, schemalessCreationEntityType)
          : null,
        tagSchemaId,
      });
    });
}

async function wrapDaoImportWithDataMods({
  changeSummary,
  daoImport,
  analyticsModule,
  useProjectLevelDataModsLogic,
}: {
  changeSummary: string | JSX.Element;
  daoImport: (dataModsHeader?: DataModificationsHeader) => void;
  useProjectLevelDataModsLogic: boolean;
  analyticsModule: keyof typeof ANALYTICS_MODULE;
}): Promise<void> {
  // Keep the spreadsheet modal open until the data mods modal is submitted.
  setShouldCloseOnRequest(false);
  try {
    await callAsyncWithDataModifications({
      changeSummary,
      onPerformAction: (dataModsHeader?) => {
        forceCloseModal();
        daoImport(dataModsHeader);
      },
      analyticsParams: {
        module: analyticsModule,
        // @ts-expect-error TODO: Should be using ActionOrigin type
        actionOrigin: 'spreadsheet_import',
        modifiedAction: InventoryRegistryDataModAction.MODIFY,
        numModifiedTotalItems: 0, // Using zero because we don't know how many will be processed
        entryId: null,
      },
      useProjectLevelDataModsLogic,
      /**
       * When project level data mods are enabled, we should trigger data mods whenever they're enabled anywhere
       * on the tenant because we cannot easily check if they're required for the specific objects that will
       * be modified
       */
      useDataModsIfEnabledAnywhere: true,
    });
  } finally {
    setShouldCloseOnRequest(true);
  }
}

function callUpdateBioentitiesFromSpreadsheetDAO(
  spreadsheetParams: SpreadsheetParams & {rowsToSkip: Array<number> | null},
  {
    inventoryFolderId,
    linkItemsFolderIds,
    selectedSchemaIds,
    schemaFieldIdsByColumnName,
    derivedColumnsMapping,
    monomerMapping,
    customSyntaxId,
    complexPolymerComponentColumnMapping,
    options,
  }: DaoImportArgs,
  dataModsHeader?: DataModificationsHeader
): JQueryPromise<SpreadsheetBulkImportDAO.UpdateBioentitiesFromSpreadsheetResponse> {
  const xhr = SpreadsheetBulkImportDAO.updateBioentitiesFromSpreadsheet(
    spreadsheetParams,
    inventoryFolderId!,
    linkItemsFolderIds,
    selectedSchemaIds.tagSchemaId,
    options[OPTIONS.REPLACE_LINK_WITH_TEXT],
    schemaFieldIdsByColumnName,
    options[OPTIONS.DELETE_EMPTY_FIELDS],
    options[OPTIONS.DELETE_EXISTING_ALIASES],
    derivedColumnsMapping,
    monomerMapping,
    complexPolymerComponentColumnMapping,
    customSyntaxId,
    dataModsHeader
  ).done(({data}: SpreadsheetBulkImportDAO.UpdateBioentitiesFromSpreadsheetResponse) => {
    const bioentityAliasInfosById: {[fileId: string]: Array<BioentityAliasInfo>} = {};
    for (const file_ of data) {
      bioentityAliasInfosById[file_.id] = file_.bioentityAliasInfos;
    }
    AppDispatcher.dispatch({
      actionType: EventConstants.BIOENTITY_ALIAS_INFO_LOAD_COMPLETED,
      bioentityAliasInfosById,
    });
    AppDispatcher.dispatch({
      actionType: EventConstants.FILE_TAGS_UPDATED,
      tagData: data,
    });
    AppDispatcher.dispatch({
      actionType: EventConstants.NOTE_BULK_UPDATE,
      items: data.filter(({note}) => note).map(({id, note}) => ({fileId: id, note})),
    });
  });
  registerTrayTaskForFolderItems(spreadsheetParams.filename, UPDATE_VERB, ModelTypeConstants.ENTITY, xhr);
  return xhr;
}

export function updateBioentitiesFromSpreadsheet(dataModsHeader?: DataModificationsHeader): void {
  dispatchForImportFromSpreadsheet(callUpdateBioentitiesFromSpreadsheetDAO, dataModsHeader);
}

export function updateBioentitiesFromSpreadsheetWithDataMods(): void {
  wrapDaoImportWithDataMods({
    changeSummary: getInventoryRegistrySpreadsheetChangeSummary([DataModsItemType.ENTITY]),
    daoImport: (dataModsHeader?) => {
      updateBioentitiesFromSpreadsheet(dataModsHeader);
    },
    analyticsModule: 'REGISTRY',
    useProjectLevelDataModsLogic: BenchlingConfig.AUDITING_DATA_MODIFICATIONS_V2_REGISTRY,
  });
}

function willUseExistingContainers(): boolean {
  const {errorData, options} = SpreadsheetBulkImportStore.getState();
  const hasExistingContainerErrors =
    errorData?.itemNameErrors?.some((err) =>
      SpreadsheetBulkImportUtil.canUseExistingContainers(err.errorCode)
    ) ?? false;

  return (options?.[OPTIONS.USE_EXISTING_CONTAINERS] ?? false) && hasExistingContainerErrors;
}

function createInventoryFilesFromSpreadsheet({
  onSubmitSpreadsheetCallback = (entityType) => {},
  dataModsHeader,
}: {
  onSubmitSpreadsheetCallback?: (entityType: OligoAwareFolderItemType | null) => void;
  dataModsHeader?: DataModificationsHeader;
} = {}): void {
  dispatchForImportFromSpreadsheet(
    (
      spreadsheetParams,
      {
        inventoryFolderId,
        linkItemsFolderIds,
        selectedSchemaIds,
        schemalessCreationEntityType,
        schemaFieldIdsByColumnName,
        options,
        derivedColumnsMapping,
        monomerMapping,
        customSyntaxId,
        complexPolymerComponentColumnMapping,
      }
    ) => {
      const resolvedEntityType = SpreadsheetBulkImportUtil.getImportEntityType(
        selectedSchemaIds,
        schemalessCreationEntityType
      );
      assertAndContinue(resolvedEntityType != null, 'Could not resolve import entity type from store state');
      onSubmitSpreadsheetCallback(resolvedEntityType);
      const xhr = SpreadsheetBulkImportDAO.createInventoryFilesFromSpreadsheet(
        spreadsheetParams,
        inventoryFolderId!,
        linkItemsFolderIds,
        selectedSchemaIds.tagSchemaId,
        selectedSchemaIds.tagSchemaId == null ? schemalessCreationEntityType : null,
        options[OPTIONS.REPLACE_LINK_WITH_TEXT],
        options[OPTIONS.ARE_SEQUENCES_CIRCULAR],
        options[OPTIONS.IMPORT_CONTAINERS] ? selectedSchemaIds.containerSchemaId : null,
        schemaFieldIdsByColumnName,
        derivedColumnsMapping,
        monomerMapping,
        complexPolymerComponentColumnMapping,
        customSyntaxId,
        dataModsHeader
      );
      registerTrayTaskForFolderItems(spreadsheetParams.filename, IMPORT_VERB, ModelTypeConstants.ENTITY, xhr);
      return xhr;
    }
  );
}

export function createInventoryFilesFromSpreadsheetWithDataMods({
  onSubmitSpreadsheetCallback = (entityType) => {},
}: {onSubmitSpreadsheetCallback?: (entityType: OligoAwareFolderItemType | null) => void} = {}): void {
  const isModifyingExistingContainers = willUseExistingContainers();

  const changeSummary = isModifyingExistingContainers
    ? getInventoryRegistrySpreadsheetChangeSummary([DataModsItemType.CONTAINER])
    : '';

  wrapDaoImportWithDataMods({
    changeSummary,
    daoImport: (dataModsHeader?) => {
      createInventoryFilesFromSpreadsheet({onSubmitSpreadsheetCallback, dataModsHeader});
    },
    analyticsModule: 'SAMPLES',
    useProjectLevelDataModsLogic:
      BenchlingConfig.AUDITING_DATA_MODIFICATIONS_V2_INVENTORY && isModifyingExistingContainers,
  });
}

function createRegistryEntitiesFromSpreadsheet(dataModsHeader?: DataModificationsHeader): void {
  dispatchForImportFromSpreadsheet(
    (
      spreadsheetParams,
      {
        inventoryFolderId,
        linkItemsFolderIds,
        selectedSchemaIds,
        schemaFieldIdsByColumnName,
        options,
        derivedColumnsMapping,
        monomerMapping,
        customSyntaxId,
        complexPolymerComponentColumnMapping,
      },
      dataModsHeader
    ) => {
      const registryImportSettings = SpreadsheetBulkImportStore.getState().registryImportSettings;
      const xhr = SpreadsheetBulkImportDAO.createRegistryEntitiesFromSpreadsheet(
        spreadsheetParams,
        registryImportSettings.registry!,
        inventoryFolderId,
        linkItemsFolderIds,
        selectedSchemaIds.tagSchemaId!,
        options[OPTIONS.ARE_SEQUENCES_CIRCULAR],
        options[OPTIONS.IMPORT_CONTAINERS] ? selectedSchemaIds.containerSchemaId : null,
        options[OPTIONS.REPLACE_LINK_WITH_TEXT],
        sanitizeRegistryImportSettingsForServer(registryImportSettings),
        schemaFieldIdsByColumnName,
        derivedColumnsMapping,
        monomerMapping,
        complexPolymerComponentColumnMapping,
        customSyntaxId,
        dataModsHeader
      );
      registerTrayTaskForFolderItems(spreadsheetParams.filename, IMPORT_VERB, ModelTypeConstants.ENTITY, xhr);
      return xhr;
    },
    dataModsHeader
  );
}

export function createTrialSamplesFromSpreadsheet(dataModsHeader?: DataModificationsHeader): void {
  const metricsImportEventId = makeAnalyticsEventId();
  const metricsImportTrayTaskEventId = makeAnalyticsEventId();
  logProfilerTimingEvent({...TRIAL_SAMPLE_IMPORT_STARTED_EVENT, id: metricsImportEventId});
  logProfilerTimingEvent({
    ...TRIAL_SAMPLE_IMPORT_STARTED_FOR_TRAY_TASK_EVENT,
    id: metricsImportTrayTaskEventId,
  });
  dispatchForImportFromSpreadsheet(
    (
      spreadsheetParams,
      {
        inventoryFolderId,
        linkItemsFolderIds,
        selectedSchemaIds,
        schemaFieldIdsByColumnName,
        options,
        derivedColumnsMapping,
        monomerMapping,
        customSyntaxId,
        complexPolymerComponentColumnMapping,
      },
      dataModsHeader
    ) => {
      const registryImportSettings = SpreadsheetBulkImportStore.getState().registryImportSettings;
      const xhr = SpreadsheetBulkImportDAO.createTrialSamplesFromSpreadsheet({
        spreadsheetParams,
        registry: registryImportSettings.registry!,
        projectFolderId: inventoryFolderId,
        linkItemsFolderIds,
        tagSchemaId: selectedSchemaIds.tagSchemaId!,
        trialSubjectSchemaId: selectedSchemaIds.trialSubjectSchemaId!,
        areSequencesCircular: options[OPTIONS.ARE_SEQUENCES_CIRCULAR],
        containerSchemaId: selectedSchemaIds.containerSchemaId!,
        replaceFileLinkWithText: options[OPTIONS.REPLACE_LINK_WITH_TEXT],
        registryImportSettings: sanitizeRegistryImportSettingsForServer(registryImportSettings),
        schemaFieldIdsByColumnName,
        derivedColumnsMapping,
        monomerMapping,
        complexPolymerComponentColumnMapping,
        customSyntaxId,
        dataModsHeader,
      });
      // Create a tray task and toast
      registerTrayTaskForSampleIngestion(spreadsheetParams.filename, xhr);
      logProfilerTimingEvent({
        ...TRIAL_SAMPLE_IMPORT_TRAY_TASK_VISIBLE_EVENT,
        id: metricsImportTrayTaskEventId,
      });

      // Emit metrics when import is successful
      xhr.done(() => {
        logProfilerTimingEvent({...TRIAL_SAMPLE_IMPORT_FINISHED_EVENT, id: metricsImportEventId});
      });

      return xhr;
    },
    dataModsHeader
  );
}

export function validateSpreadsheetForTrialSamples(): void {
  const metricsEventId = makeAnalyticsEventId();
  logProfilerTimingEvent({...TRIAL_SAMPLE_VALIDATION_STARTED_EVENT, id: metricsEventId});
  dispatchForValidateSpreadsheet(
    (
      spreadsheetParams,
      {
        inventoryFolderId,
        linkItemsFolderIds,
        selectedSchemaIds,
        schemaFieldIdsByColumnName,
        options,
        derivedColumnsMapping,
        monomerMapping,
        customSyntaxId,
        complexPolymerComponentColumnMapping,
      }
    ) => {
      const registryImportSettings = SpreadsheetBulkImportStore.getState().registryImportSettings;
      const xhr = SpreadsheetBulkImportDAO.validateSpreadsheetForTrialSamples({
        spreadsheetParams,
        registry: registryImportSettings.registry!,
        projectFolderId: inventoryFolderId,
        linkItemsFolderIds,
        tagSchemaId: selectedSchemaIds.tagSchemaId!,
        trialSubjectSchemaId: selectedSchemaIds.trialSubjectSchemaId!,
        areSequencesCircular: options[OPTIONS.ARE_SEQUENCES_CIRCULAR],
        containerSchemaId: selectedSchemaIds.containerSchemaId!,
        replaceFileLinkWithText: options[OPTIONS.REPLACE_LINK_WITH_TEXT],
        registryImportSettings: sanitizeRegistryImportSettingsForServer(registryImportSettings),
        schemaFieldIdsByColumnName,
        derivedColumnsMapping,
        monomerMapping,
        complexPolymerComponentColumnMapping,
        customSyntaxId,
      });
      xhr.done(() => {
        logProfilerTimingEvent({...TRIAL_SAMPLE_VALIDATION_FINISHED_EVENT, id: metricsEventId});
      });
      return xhr;
    }
  );
}

export function createRegistryEntitiesFromSpreadsheetWithDataMods(): void {
  // We only need to record data mods if there will be duplicates merged during registration, or when the user
  // elects to move the newly created entities into existing containers
  const mergingDuplicatesCount =
    SpreadsheetBulkImportStore.getState().errorData?.registryWarnings?.filter(
      (err) => err.errorCode === RowErrorCode.REGISTRY_ERROR && err.primaryDuplicateItemApiId != null
    ).length ?? 0;
  const isMergingDuplicates = mergingDuplicatesCount > 0;

  const isModifyingExistingContainers = willUseExistingContainers();

  const changeSummary = (
    <>
      {isModifyingExistingContainers && (
        <p>{getInventoryRegistrySpreadsheetChangeSummary([DataModsItemType.CONTAINER])}</p>
      )}
      {isMergingDuplicates && getRegisterMergeOnlyChangeSummary(mergingDuplicatesCount)}
    </>
  );

  wrapDaoImportWithDataMods({
    changeSummary,
    daoImport: (dataModsHeader?) => {
      createRegistryEntitiesFromSpreadsheet(dataModsHeader);
    },
    analyticsModule: 'REGISTRY',
    useProjectLevelDataModsLogic:
      BenchlingConfig.AUDITING_DATA_MODIFICATIONS_V2_REGISTRY &&
      (isMergingDuplicates || isModifyingExistingContainers),
  });
}

export function validateSpreadsheetForTransferStorables(): void {
  dispatchForValidateSpreadsheet((spreadsheetParams, {options}) =>
    SpreadsheetBulkImportDAO.validateSpreadsheetForTransferStorables(
      spreadsheetParams,
      options[OPTIONS.CLEAR_SAMPLE_ROLES_FOR_EMPTY_CELLS]
    )
  );
}

function transferStorablesFromSpreadsheet(dataModsHeader?: DataModificationsHeader): void {
  dispatchForImportFromSpreadsheet((spreadsheetParams, {options}, dataModsHeader) => {
    const xhr = SpreadsheetBulkImportDAO.transferStorablesFromSpreadsheet(
      spreadsheetParams,
      options[OPTIONS.CLEAR_SAMPLE_ROLES_FOR_EMPTY_CELLS],
      dataModsHeader
    ).done(
      ({invalidatableStorableIds}: SpreadsheetBulkImportDAO.TransferStorablesFromSpreadsheetResponse) => {
        AppDispatcher.dispatch({
          actionType: EventConstants.SPREADSHEET_BULK_TRANSFER_STORABLES_COMPLETED,
          invalidatableStorableIds,
        });
      }
    );
    registerTrayTaskForStorables(spreadsheetParams.filename, IMPORT_VERB, 'transfer', xhr);
    return xhr;
  }, dataModsHeader);
}

export function transferStorablesFromSpreadsheetWithDataMods(): void {
  wrapDaoImportWithDataMods({
    changeSummary: getInventoryRegistrySpreadsheetChangeSummary([DataModsItemType.CONTAINER_OR_PLATE_WELL]),
    daoImport: (dataModsHeader?) => {
      transferStorablesFromSpreadsheet(dataModsHeader);
    },
    analyticsModule: 'SAMPLES',
    useProjectLevelDataModsLogic: BenchlingConfig.AUDITING_DATA_MODIFICATIONS_V2_INVENTORY,
  });
}

export function validateSpreadsheetForCreateContainers(): void {
  dispatchForValidateSpreadsheet(
    (spreadsheetParams, {selectedSchemaIds, schemaFieldIdsByColumnName, linkItemsFolderIds}) =>
      SpreadsheetBulkImportDAO.validateSpreadsheetForCreateContainers(
        spreadsheetParams,
        selectedSchemaIds.containerSchemaId!,
        schemaFieldIdsByColumnName,
        getSourceIdForStorableBulkAction(),
        linkItemsFolderIds
      )
  );
}

export function createContainersFromSpreadsheet(): void {
  dispatchForImportFromSpreadsheet(
    (
      spreadsheetParams,
      {inventoryFolderId, selectedSchemaIds, schemaFieldIdsByColumnName, options, linkItemsFolderIds}
    ) => {
      const xhr = SpreadsheetBulkImportDAO.createContainersFromSpreadsheet(
        spreadsheetParams,
        selectedSchemaIds.containerSchemaId!,
        schemaFieldIdsByColumnName,
        getSourceIdForStorableBulkAction(),
        linkItemsFolderIds
      );
      registerTrayTaskForStorables(
        spreadsheetParams.filename,
        IMPORT_VERB,
        ModelTypeConstants.CONTAINER,
        xhr
      );
      return xhr;
    }
  );
}

export function validateSpreadsheetForUpdateContainers(): void {
  dispatchForValidateSpreadsheet(
    (spreadsheetParams, {selectedSchemaIds, schemaFieldIdsByColumnName, linkItemsFolderIds, options}) =>
      SpreadsheetBulkImportDAO.validateSpreadsheetForUpdateContainers(
        spreadsheetParams,
        selectedSchemaIds.containerSchemaId!,
        schemaFieldIdsByColumnName,
        linkItemsFolderIds,
        options[OPTIONS.DELETE_EMPTY_FIELDS],
        options[OPTIONS.CLEAR_SAMPLE_ROLES_FOR_EMPTY_CELLS]
      )
  );
}

function updateContainersFromSpreadsheet(dataModsHeader?: DataModificationsHeader): void {
  dispatchForImportFromSpreadsheet(
    (
      spreadsheetParams,
      {selectedSchemaIds, schemaFieldIdsByColumnName, options, linkItemsFolderIds},
      dataModsHeader
    ) => {
      const xhr = SpreadsheetBulkImportDAO.updateContainersFromSpreadsheet(
        spreadsheetParams,
        selectedSchemaIds.containerSchemaId!,
        schemaFieldIdsByColumnName,
        linkItemsFolderIds,
        options[OPTIONS.DELETE_EMPTY_FIELDS],
        options[OPTIONS.CLEAR_SAMPLE_ROLES_FOR_EMPTY_CELLS],
        dataModsHeader
      );
      registerTrayTaskForStorables(
        spreadsheetParams.filename,
        UPDATE_VERB,
        ModelTypeConstants.CONTAINER,
        xhr
      );
      return xhr;
    },
    dataModsHeader
  );
}

export async function updateContainersFromSpreadsheetWithDataMods(): Promise<void> {
  await wrapDaoImportWithDataMods({
    changeSummary: getInventoryRegistrySpreadsheetChangeSummary([DataModsItemType.CONTAINER]),
    daoImport: (dataModsHeader?) => {
      updateContainersFromSpreadsheet(dataModsHeader);
    },
    analyticsModule: 'SAMPLES',
    useProjectLevelDataModsLogic: BenchlingConfig.AUDITING_DATA_MODIFICATIONS_V2_INVENTORY,
  });
}

export function validateSpreadsheetImportForMixtureIngredients(): void {
  dispatchForValidateSpreadsheet((spreadsheetParams, {inventoryFolderId, linkItemsFolderIds, options}) =>
    SpreadsheetBulkImportDAO.validateSpreadsheetImportForMixtureIngredients(
      spreadsheetParams,
      inventoryFolderId!,
      linkItemsFolderIds,
      options[OPTIONS.CLEAR_INGREDIENT_FIELDS_FOR_EMPTY_CELLS]
    )
  );
}

function callImportMixtureIngredientsFromSpreadsheetDAO(
  spreadsheetParams: SpreadsheetParams & {rowsToSkip: Array<number> | null},
  {inventoryFolderId, linkItemsFolderIds, options}: DaoImportArgs,
  dataModsHeader?: DataModificationsHeader
): JQueryPromise<SpreadsheetBulkImportDAO.ImportMixtureIngredientsFromSpreadsheetResponse> {
  const xhr = SpreadsheetBulkImportDAO.importMixtureIngredientsFromSpreadsheet(
    spreadsheetParams,
    inventoryFolderId!,
    linkItemsFolderIds,
    options[OPTIONS.CLEAR_INGREDIENT_FIELDS_FOR_EMPTY_CELLS],
    dataModsHeader
  ).done(
    ({
      mixtureSpecificAttributes,
    }: SpreadsheetBulkImportDAO.ImportMixtureIngredientsFromSpreadsheetResponse) => {
      writeFragmentsToCache(getFragmentWrapper('MixtureSpecificAttributes'), mixtureSpecificAttributes);
    }
  );
  registerTrayTaskForFolderItems(
    spreadsheetParams.filename,
    IMPORT_VERB,
    `${ModelTypeConstants.MIXTURE} ${ModelTypeConstants.INGREDIENT}`,
    xhr
  );
  return xhr;
}

function importMixtureIngredientsFromSpreadsheet(dataModsHeader?: DataModificationsHeader): void {
  dispatchForImportFromSpreadsheet(callImportMixtureIngredientsFromSpreadsheetDAO, dataModsHeader);
}

export function importMixtureIngredientsFromSpreadsheetWithDataMods(): void {
  wrapDaoImportWithDataMods({
    changeSummary: getInventoryRegistrySpreadsheetChangeSummary([DataModsItemType.ENTITY]),
    daoImport: (dataModsHeader?) => {
      importMixtureIngredientsFromSpreadsheet(dataModsHeader);
    },
    analyticsModule: 'REGISTRY',
    useProjectLevelDataModsLogic: BenchlingConfig.AUDITING_DATA_MODIFICATIONS_V2_REGISTRY,
  });
}

export function getUpdatedRowsForDataTableImport(
  dataTable: DataTable,
  onImportRows: (rows: Array<ImportedRowUpdate>) => void
): void {
  dispatchForImportFromSpreadsheet(
    (spreadsheetParams, {selectedSchemaIds, schemaFieldIdsByColumnName, options}) => {
      const columns = SpreadsheetBulkImportStore.getState().fileData!.columns;
      const tableColumnNamesByColumnName = SpreadsheetBulkImportUtil.getTableColumnNamesByColumnName(columns);
      return asJQueryPromise(
        withLoadingScreen(
          Promise.resolve(
            SpreadsheetBulkImportDAO.getUpdatedRowsForDataTableImport(
              spreadsheetParams,
              tableColumnNamesByColumnName,
              dataTable
            )
          ).then(({importedRows}: SpreadsheetBulkImportDAO.GetUpdatedRowsForDataTableImportResponse) => {
            onImportRows(importedRows);
          }) as Promise<SpreadsheetBulkImportDAO.GetUpdatedRowsForDataTableImportResponse>,
          'Importing...'
        )
      );
    }
  );
}

export function getNewRowsForDataTableImport(
  entryOrStageEntryId: string,
  dataTableConfig: DataTableConfig,
  onImportRows: (importedRows: Array<ImportedDataTableRow>) => void
): void {
  const stageEntryId =
    DataTableUtil.getDataTableConfigType(dataTableConfig) === DATA_TABLE_TYPE.STAGE_RUN_SAMPLE_LINK_TABLE
      ? entryOrStageEntryId
      : null;

  dispatchForImportFromSpreadsheet(
    (spreadsheetParams, {selectedSchemaIds, schemaFieldIdsByColumnName, options}) => {
      const columns = SpreadsheetBulkImportStore.getState().fileData!.columns;
      const tableColumnNamesByColumnName = SpreadsheetBulkImportUtil.getTableColumnNamesByColumnName(columns);
      return asJQueryPromise(
        withLoadingScreen(
          Promise.resolve(
            SpreadsheetBulkImportDAO.getNewRowsForDataTableImport(
              spreadsheetParams,
              tableColumnNamesByColumnName,
              dataTableConfig,
              stageEntryId
            )
          ).then(({importedRows}: SpreadsheetBulkImportDAO.GetNewRowsForDataTableImportResponse) => {
            onImportRows(importedRows);
          }) as Promise<SpreadsheetBulkImportDAO.GetNewRowsForDataTableImportResponse>,
          'Importing...'
        )
      );
    }
  );
}

export function getNewRowsForTransferTableImport(
  fileId: string,
  onImportRows: (rows: Array<ImportedTransferTableRow>) => void
): void {
  LoadingScreen.show('Importing...');

  dispatchForImportFromSpreadsheet((spreadsheetParams, {schemaFieldIdsByColumnName}) =>
    SpreadsheetBulkImportDAO.getNewRowsForTransferTableImport(
      spreadsheetParams,
      schemaFieldIdsByColumnName,
      fileId
    )
      .done(({importedRows}: SpreadsheetBulkImportDAO.GetNewRowsForTransferTableImportResponse) => {
        onImportRows(importedRows);
      })
      .always(() => {
        LoadingScreen.hide();
      })
  );
}

export function getNewRowsForRegistrationTableImport(
  onImportRows: (rows: Array<ImportedStructuredTableRow>) => void
): void {
  LoadingScreen.show('Importing...');

  dispatchForImportFromSpreadsheet((spreadsheetParams, {schemaFieldIdsByColumnName}) =>
    SpreadsheetBulkImportDAO.getNewRowsForRegistrationTableImport(
      spreadsheetParams,
      schemaFieldIdsByColumnName
    )
      .done(({importedRows}: SpreadsheetBulkImportDAO.GetNewRowsForRegistrationTableImportResponse) => {
        onImportRows(importedRows);
      })
      .always(() => {
        LoadingScreen.hide();
      })
  );
}

export function getNewRowsForStructuredTableImport(
  onImportRows: (rows: Array<ImportedStructuredTableRow>) => void
): void {
  LoadingScreen.show('Importing...');

  dispatchForImportFromSpreadsheet((spreadsheetParams, {schemaFieldIdsByColumnName}) =>
    SpreadsheetBulkImportDAO.getNewRowsForStructuredTableImport(spreadsheetParams, schemaFieldIdsByColumnName)
      .done(({parsedRows}: SpreadsheetBulkImportDAO.GetNewRowsForStructuredTableImportResponse) => {
        onImportRows(parsedRows);
      })
      .always(() => {
        LoadingScreen.hide();
      })
  );
}

export function validateNewSampleGroups(): void {
  dispatchForValidateSpreadsheet(
    (spreadsheetParams, {selectedSchemaIds, schemaFieldIdsByColumnName, options}) => {
      const columns = SpreadsheetBulkImportStore.getState().fileData!.columns;
      const sampleTypesByColumnName = SpreadsheetBulkImportUtil.getSampleTypesByColumnName(columns);
      return SpreadsheetBulkImportDAO.validateNewSampleGroups(
        spreadsheetParams,
        selectedSchemaIds.sampleGroupSchemaId!,
        schemaFieldIdsByColumnName,
        sampleTypesByColumnName
      );
    }
  );
}

export function getNewSampleGroups(onImportRows: (rows: Array<ImportedRow>) => void): void {
  LoadingScreen.show('Importing...');

  dispatchForImportFromSpreadsheet((spreadsheetParams, {selectedSchemaIds, schemaFieldIdsByColumnName}) => {
    const columns = SpreadsheetBulkImportStore.getState().fileData!.columns;
    const sampleTypesByColumnName = SpreadsheetBulkImportUtil.getSampleTypesByColumnName(columns);
    return SpreadsheetBulkImportDAO.getNewSampleGroups(
      spreadsheetParams,
      selectedSchemaIds.sampleGroupSchemaId!,
      schemaFieldIdsByColumnName,
      sampleTypesByColumnName
    )
      .done(({importedRows}: SpreadsheetBulkImportDAO.GetNewSampleGroupsResponse) => {
        onImportRows(importedRows);
      })
      .always(() => {
        LoadingScreen.hide();
      });
  });
}

function validateSpreadsheetForCreateGrids(gridSchemaIdAccessorKey: keyof SelectedSchemaIds): void {
  dispatchForValidateSpreadsheet(
    (spreadsheetParams, {selectedSchemaIds, schemaFieldIdsByColumnName, linkItemsFolderIds}) =>
      SpreadsheetBulkImportDAO.validateSpreadsheetForCreateGrids(
        spreadsheetParams,
        selectedSchemaIds[gridSchemaIdAccessorKey]!,
        schemaFieldIdsByColumnName,
        getSourceIdForStorableBulkAction(),
        linkItemsFolderIds
      )
  );
}

export function validateSpreadsheetForCreatePlates(): void {
  validateSpreadsheetForCreateGrids('plateSchemaId');
}

export function validateSpreadsheetForCreateBoxes(): void {
  validateSpreadsheetForCreateGrids('boxSchemaId');
}

export function validateSpreadsheetForCreateLocations(): void {
  dispatchForValidateSpreadsheet(
    (spreadsheetParams, {selectedSchemaIds, schemaFieldIdsByColumnName, linkItemsFolderIds}) =>
      SpreadsheetBulkImportDAO.validateSpreadsheetForCreateLocations(
        spreadsheetParams,
        selectedSchemaIds.locationSchemaId!,
        schemaFieldIdsByColumnName,
        getSourceIdForStorableBulkAction(),
        linkItemsFolderIds
      )
  );
}

export function createGridsFromSpreadsheet(
  gridSchemaIdAccessorKey: keyof SelectedSchemaIds,
  pluralItemWord: StorableItemType | 'transfer'
): void {
  dispatchForImportFromSpreadsheet(
    (spreadsheetParams, {selectedSchemaIds, schemaFieldIdsByColumnName, linkItemsFolderIds}) => {
      const xhr = SpreadsheetBulkImportDAO.createGridsFromSpreadsheet(
        spreadsheetParams,
        selectedSchemaIds[gridSchemaIdAccessorKey]!,
        schemaFieldIdsByColumnName,
        getSourceIdForStorableBulkAction(),
        linkItemsFolderIds
      );
      registerTrayTaskForStorables(spreadsheetParams.filename, IMPORT_VERB, pluralItemWord, xhr);
      return xhr;
    }
  );
}

export function validateSpreadsheetForUpdatePlates(): void {
  validateSpreadsheetForUpdateGrids('plateSchemaId');
}

export function validateSpreadsheetForUpdateBoxes(): void {
  validateSpreadsheetForUpdateGrids('boxSchemaId');
}

function validateSpreadsheetForUpdateGrids(gridSchemaIdAccessorKey: keyof SelectedSchemaIds): void {
  dispatchForValidateSpreadsheet(
    (spreadsheetParams, {selectedSchemaIds, schemaFieldIdsByColumnName, linkItemsFolderIds, options}) =>
      SpreadsheetBulkImportDAO.validateSpreadsheetForUpdateGrids(
        spreadsheetParams,
        selectedSchemaIds[gridSchemaIdAccessorKey]!,
        schemaFieldIdsByColumnName,
        getSourceIdForStorableBulkAction(),
        linkItemsFolderIds,
        options[OPTIONS.DELETE_EMPTY_FIELDS]
      )
  );
}

export function validateSpreadsheetForUpdateLocations(): void {
  dispatchForValidateSpreadsheet(
    (spreadsheetParams, {selectedSchemaIds, schemaFieldIdsByColumnName, linkItemsFolderIds, options}) =>
      SpreadsheetBulkImportDAO.validateSpreadsheetForUpdateLocations(
        spreadsheetParams,
        selectedSchemaIds.locationSchemaId!,
        schemaFieldIdsByColumnName,
        getSourceIdForStorableBulkAction(),
        linkItemsFolderIds,
        options[OPTIONS.DELETE_EMPTY_FIELDS]
      )
  );
}

function updatePlatesFromSpreadsheet(dataModsHeader?: DataModificationsHeader): void {
  dispatchForImportFromSpreadsheet(
    getUpdateGridsFromSpreadsheetDAO('plateSchemaId', ModelTypeConstants.PLATE),
    dataModsHeader
  );
}

export function updatePlatesFromSpreadsheetWithDataMods(): void {
  wrapDaoImportWithDataMods({
    changeSummary: getInventoryRegistrySpreadsheetChangeSummary([DataModsItemType.PLATE]),
    daoImport: (dataModsHeader?) => {
      updatePlatesFromSpreadsheet(dataModsHeader);
    },
    analyticsModule: 'SAMPLES',
    useProjectLevelDataModsLogic: BenchlingConfig.AUDITING_DATA_MODIFICATIONS_V2_INVENTORY,
  });
}

function updateBoxesFromSpreadsheet(dataModsHeader?: DataModificationsHeader): void {
  dispatchForImportFromSpreadsheet(
    getUpdateGridsFromSpreadsheetDAO('boxSchemaId', ModelTypeConstants.BOX),
    dataModsHeader
  );
}

export function updateBoxesFromSpreadsheetWithDataMods(): void {
  wrapDaoImportWithDataMods({
    changeSummary: getInventoryRegistrySpreadsheetChangeSummary([DataModsItemType.BOX]),
    daoImport: (dataModsHeader?) => {
      updateBoxesFromSpreadsheet(dataModsHeader);
    },
    analyticsModule: 'SAMPLES',
    useProjectLevelDataModsLogic: BenchlingConfig.AUDITING_DATA_MODIFICATIONS_V2_INVENTORY,
  });
}

function getUpdateGridsFromSpreadsheetDAO(
  gridSchemaIdAccessorKey: keyof SelectedSchemaIds,
  pluralItemWord: StorableItemType | 'transfer'
): (
  spreadsheetParams: SpreadsheetParams & {rowsToSkip: Array<number> | null},
  args: DaoImportArgs,
  dataModsHeader?: DataModificationsHeader
) => JQueryPromise<SpreadsheetBulkImportDAO.CreateOrUpdateStorablesFromSpreadsheetResponse> {
  return (
    spreadsheetParams,
    {selectedSchemaIds, schemaFieldIdsByColumnName, linkItemsFolderIds, options},
    dataModsHeader
  ) => {
    const xhr = SpreadsheetBulkImportDAO.updateGridsFromSpreadsheet(
      spreadsheetParams,
      selectedSchemaIds[gridSchemaIdAccessorKey]!,
      schemaFieldIdsByColumnName,
      getSourceIdForStorableBulkAction(),
      linkItemsFolderIds,
      options[OPTIONS.DELETE_EMPTY_FIELDS],
      dataModsHeader
    );
    registerTrayTaskForStorables(spreadsheetParams.filename, UPDATE_VERB, pluralItemWord, xhr);
    return xhr;
  };
}

function updateLocationsFromSpreadsheet(dataModsHeader?: DataModificationsHeader): void {
  dispatchForImportFromSpreadsheet(
    (
      spreadsheetParams,
      {selectedSchemaIds, schemaFieldIdsByColumnName, linkItemsFolderIds, options},
      dataModsHeader
    ) => {
      const xhr = SpreadsheetBulkImportDAO.updateLocationsFromSpreadsheet(
        spreadsheetParams,
        selectedSchemaIds.locationSchemaId!,
        schemaFieldIdsByColumnName,
        getSourceIdForStorableBulkAction(),
        linkItemsFolderIds,
        options[OPTIONS.DELETE_EMPTY_FIELDS],
        dataModsHeader
      );
      registerTrayTaskForStorables(spreadsheetParams.filename, UPDATE_VERB, ModelTypeConstants.LOCATION, xhr);
      return xhr;
    },
    dataModsHeader
  );
}

export function updateLocationsFromSpreadsheetWithDataMods(): void {
  wrapDaoImportWithDataMods({
    changeSummary: getInventoryRegistrySpreadsheetChangeSummary([DataModsItemType.LOCATION]),
    daoImport: (dataModsHeader?) => {
      updateLocationsFromSpreadsheet(dataModsHeader);
    },
    analyticsModule: 'SAMPLES',
    useProjectLevelDataModsLogic: BenchlingConfig.AUDITING_DATA_MODIFICATIONS_V2_INVENTORY,
  });
}

export function createPlatesFromSpreadsheet(): void {
  createGridsFromSpreadsheet('plateSchemaId', ModelTypeConstants.PLATE);
}

export function createBoxesFromSpreadsheet(): void {
  createGridsFromSpreadsheet('boxSchemaId', ModelTypeConstants.BOX);
}

export function createLocationsFromSpreadsheet(): void {
  dispatchForImportFromSpreadsheet(
    (spreadsheetParams, {selectedSchemaIds, schemaFieldIdsByColumnName, linkItemsFolderIds}) => {
      const xhr = SpreadsheetBulkImportDAO.createLocationsFromSpreadsheet(
        spreadsheetParams,
        selectedSchemaIds.locationSchemaId!,
        schemaFieldIdsByColumnName,
        getSourceIdForStorableBulkAction(),
        linkItemsFolderIds
      );
      registerTrayTaskForStorables(spreadsheetParams.filename, IMPORT_VERB, ModelTypeConstants.LOCATION, xhr);
      return xhr;
    }
  );
}

export function removeSpreadsheet(): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.SPREADSHEET_BULK_IMPORT_REMOVE_SPREADSHEET,
  });
}

export function setColumns(columns: Array<SpreadsheetColumn>): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.SPREADSHEET_BULK_IMPORT_SET_COLUMNS,
    columns,
  });
}

export function setDerivedColumns(derivedColumns: Array<DerivedColumn>): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.SPREADSHEET_BULK_IMPORT_SET_DERIVED_COLUMNS,
    derivedColumns,
  });
}

export interface ImportFlowArgs {
  importType: IMPORT_TYPE | undefined;
  panes: Array<Pane>;
  initialBatchSchemaId?: string | null;
  initialContainerSchemaId?: string | null;
  initialDataTableKeyFields?: Array<COLUMN_TYPE>;
  initialSchemalessCreationEntityType?: SchemalessBulkImportSupportedOligoAwareTypes;
  initialFolder?: {
    folderId: string | null;
    registryId?: string | null;
  } | null;
  initialRegistry?: Source | null;
  initialResultSchemaId?: string | null;
  initialTagSchemaId?: string | null;
  initialTableColumnNames?: Array<string>;
  initialSampleGroupSchemaId?: string | null;
  initialPlateSchemaId?: string | null;
  initialBoxSchemaId?: string | null;
  initialProcessTaskSchemaId?: string | null;
  initialProcessOutputSchemaId?: string | null;
  initialTrialSubjectSchemaId?: string | null;
  initialRawText?: string;
  initialPaneKey?: PaneKey;
}

export function beginImportFlow({
  panes,
  importType,
  initialRegistry,
  initialFolder,
  initialBatchSchemaId,
  initialContainerSchemaId,
  initialTagSchemaId,
  initialSampleGroupSchemaId,
  initialResultSchemaId,
  initialProcessTaskSchemaId,
  initialProcessOutputSchemaId,
  initialTrialSubjectSchemaId,
  initialPlateSchemaId,
  initialBoxSchemaId,
  initialTableColumnNames,
  initialDataTableKeyFields,
  initialSchemalessCreationEntityType,
  initialRawText,
  initialPaneKey,
}: ImportFlowArgs): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.SPREADSHEET_BULK_IMPORT_BEGIN_IMPORT_FLOW,
    panes,
    importType,
    initialRegistry,
    initialFolder,
    initialBatchSchemaId,
    initialContainerSchemaId,
    initialTagSchemaId,
    initialSampleGroupSchemaId,
    initialResultSchemaId,
    initialProcessTaskSchemaId,
    initialProcessOutputSchemaId,
    initialTrialSubjectSchemaId,
    initialPlateSchemaId,
    initialBoxSchemaId,
    initialTableColumnNames,
    initialDataTableKeyFields,
    initialSchemalessCreationEntityType,
    initialRawText,
    initialPaneKey,
  });
}

export function setOptions(options: {[key in OPTIONS]?: boolean}): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.SPREADSHEET_BULK_IMPORT_SET_OPTIONS,
    options,
  });
}

export function setImportSetting(settings: ImportSettings): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.SPREADSHEET_BULK_IMPORT_SET_IMPORT_SETTING,
    ...settings,
  });
}

export function setTimeLimit(_timeLimit: number): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.SPREADSHEET_BULK_IMPORT_SET_TIME_LIMITS,
    _timeLimit,
  });
}

export function setSavedColumnMappingName(spreadsheetColumnsMappingName: string | null): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.SPREADSHEET_BULK_IMPORT_SET_SPREADSHEET_COLUMNS_MAPPING_NAME,
    spreadsheetColumnsMappingName,
  });
}

export function goToNextPane(): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.SPREADSHEET_BULK_IMPORT_GO_TO_NEXT_PANE,
  });
}

export function goToPrevPane(): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.SPREADSHEET_BULK_IMPORT_GO_TO_PREV_PANE,
  });
}

export function goToPaneWithKey(paneKey: PaneKey): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.SPREADSHEET_BULK_IMPORT_GO_TO_PANE_WITH_KEY,
    paneKey,
  });
}

function setShouldCloseOnRequest(shouldCloseOnRequest: boolean): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.SPREADSHEET_BULK_IMPORT_SET_SHOULD_CLOSE_ON_REQUEST,
    shouldCloseOnRequest,
  });
}

export function updateMonomerMapping(monomerMapping: MonomerMapping): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.SPREADSHEET_BULK_IMPORT_UPDATE_MONOMER_MAPPING,
    monomerMapping,
  });
}

export async function downloadInvalidMolecules(resolvableErrorStatuses: {
  [key in OPTIONS]?: boolean;
}): Promise<void> {
  const {errorData, fileData} = SpreadsheetBulkImportStore.getState();
  const url = fileData!.url;
  const errorMessages = SpreadsheetBulkImportUtil.getErrorMessages(errorData!, resolvableErrorStatuses);
  const formattedErrorMessages = Array.from(errorMessages).map(([rowIndex, rowErrorData]) => ({
    rowIndex,
    errorMessage: rowErrorData.map((rowErrorDatum) => rowErrorDatum.message).join(', '),
  }));
  const response = await SpreadsheetBulkImportDAO.downloadInvalidMolecules(url!, formattedErrorMessages);
  redirectForDownload(response.s3Location);
  notifications.success('Downloading invalid molecules...');
}
